import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { LuEye, LuEyeOff } from "react-icons/lu";
import AuthLayout, { AuthField } from "../components/auth/AuthLayout";
import PhoneInput from "../components/PhoneInput";
import { useLanguage } from "../context/LanguageContext";
import "../styles/Auth.scss";
import { loginWithGoogle, register as registerUser, storeAuthSession } from "../api/auth";
import useGoogleSignIn from "../hooks/useGoogleSignIn";

const RegisterPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const redirectTimeoutRef = useRef(null);
  const EMAIL_MAX_LENGTH = 128;
  const PASSWORD_MAX_LENGTH = 64;
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordPattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{9,}$/;
  const phonePattern = /^\+[1-9]\d{7,14}$/;
  const [form, setForm] = useState({
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    terms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });
  const { requestCredential, isLoading: isGoogleLoading } = useGoogleSignIn();

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePhoneChange = (value) => {
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!form.email || !form.password || !form.phone) {
      setError(t("auth.register.error.required", "Please enter an email, phone number and password."));
      return;
    }

    if (form.email.length > EMAIL_MAX_LENGTH) {
      setError(
        t(
          "auth.register.error.emailTooLong",
          "Email must be 128 characters or fewer."
        )
      );
      return;
    }

    if (!emailPattern.test(form.email.trim())) {
      setError(t("auth.register.error.emailInvalid", "Please enter a valid email address."));
      return;
    }

    if (form.password.length > PASSWORD_MAX_LENGTH) {
      setError(
        t(
          "auth.register.error.passwordTooLong",
          "Password must be 64 characters or fewer."
        )
      );
      return;
    }

    if (!passwordPattern.test(form.password)) {
      setError(
        t(
          "auth.register.error.passwordInvalid",
          "Password must include a capital letter, a number, a special symbol, and be at least 9 characters long."
        )
      );
      return;
    }

    if (!phonePattern.test(form.phone.trim())) {
      setError(
        t(
          "auth.register.error.phoneInvalid",
          "Please enter a valid phone number in international format (e.g., +43123456789)."
        )
      );
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError(t("auth.register.error.mismatch", "Passwords do not match."));
      return;
    }

    if (!form.terms) {
      setError(t("auth.register.error.terms", "You need to accept the legal policies."));
      return;
    }

    setIsSubmitting(true);
    try {
      await registerUser(form.email, form.password, form.phone);
      setSuccess(t("auth.register.success", "Account created. You can log in now."));
      const email = form.email;
      setForm({
        email: "",
        password: "",
        confirmPassword: "",
        phone: "",
        terms: false,
      });
      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
      redirectTimeoutRef.current = window.setTimeout(() => {
        navigate("/login", { replace: true, state: { emailPrefill: email, registered: true } });
      }, 1200);
    } catch (err) {
      setError(
        err?.message ??
          t("auth.register.error.generic", "Something went wrong. Please try again later.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildPasswordToggleLabel = (isVisible) =>
    isVisible
      ? t("auth.password.hide", "Hide password")
      : t("auth.password.show", "Show password");

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const consumePendingGoogleToken = useCallback(async () => {
    const pendingToken = sessionStorage.getItem("wayli.google.oauth.token");
    if (!pendingToken) return;

    setError("");
    setSuccess("");
    setIsSubmitting(true);

    try {
      const result = await loginWithGoogle(pendingToken, true);
      storeAuthSession(result?.expiresUtc, true);
      sessionStorage.removeItem("wayli.google.oauth.token");
      sessionStorage.removeItem("wayli.google.oauth.state");
      sessionStorage.removeItem("wayli.google.oauth.intent");

      setSuccess(t("auth.register.googleSuccess", "Signed in with Google. Redirecting..."));

      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
      redirectTimeoutRef.current = window.setTimeout(() => {
        navigate("/profile");
      }, 1000);
    } catch (err) {
      const message =
        err?.message ??
        t("auth.register.error.google", "We couldn't complete Google sign up. Please try again.");
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    consumePendingGoogleToken();
  }, [consumePendingGoogleToken]);

  const handleGoogleSignup = async () => {
    setError("");
    setSuccess("");
    setIsSubmitting(true);

    try {
      const credential = await requestCredential({ intent: "register" });
      const result = await loginWithGoogle(credential, true);
      storeAuthSession(result?.expiresUtc, true);
      setSuccess(t("auth.register.googleSuccess", "Signed in with Google. Redirecting..."));

      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
      redirectTimeoutRef.current = window.setTimeout(() => {
        navigate("/profile");
      }, 1000);
    } catch (err) {
      const message =
        err?.message ??
        t("auth.register.error.google", "We couldn't complete Google sign up. Please try again.");
      if (typeof message === "string" && message.toLowerCase().includes("cancel")) {
        return;
      }
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(
    () => () => {
      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
    },
    []
  );

  return (
    <AuthLayout
      eyebrow={t("auth.register.eyebrow", "Join the crew")}
      title={t("auth.register.title", "Create your Wayli account")}
      lead={t(
        "auth.register.lead",
        "Save your favourite spots, earn rewards, and get paddle-ready updates."
      )}
      illustration={
        <img
          src="/assets/pos_reg.png"
          alt={t("auth.media.registerAlt", "Friends preparing to paddle boards")}
          className="auth-illustration"
        />
      }
      footer={
        <p>
          {t("auth.register.footer.text", "Already have an account?")}{" "}
          <Link to="/login">{t("auth.register.footer.link", "Log in")}</Link>
        </p>
      }
    >
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <AuthField
          id="register-email"
          label={t("auth.fields.email", "Email")}
          type="email"
          name="email"
          autoComplete="email"
          required
          maxLength={EMAIL_MAX_LENGTH}
          value={form.email}
          onChange={handleChange}
        />
        <label className="auth-field" htmlFor="register-phone">
          <span>{t("auth.fields.phone", "Phone")}</span>
          <PhoneInput
            id="register-phone"
            name="phone"
            value={form.phone}
            onChange={handlePhoneChange}
            required
            autoComplete="tel"
            placeholder={t("auth.fields.phonePlaceholder", "Phone number")}
            className="auth-phone"
            selectClassName="auth-phone__select"
            inputClassName="auth-phone__input"
            countryLabel={t("auth.fields.countryCode", "Country code")}
            numberLabel={t("auth.fields.phone", "Phone")}
          />
        </label>
        <AuthField
          id="register-password"
          label={t("auth.fields.password", "Password")}
          type={passwordVisibility.password ? "text" : "password"}
          name="password"
          autoComplete="new-password"
          required
          maxLength={PASSWORD_MAX_LENGTH}
          value={form.password}
          onChange={handleChange}
          endAdornment={
            <button
              type="button"
              className="auth-field__password-toggle"
              onClick={() => togglePasswordVisibility("password")}
              aria-label={buildPasswordToggleLabel(passwordVisibility.password)}
              aria-pressed={passwordVisibility.password}
              title={buildPasswordToggleLabel(passwordVisibility.password)}
            >
              {passwordVisibility.password ? (
                <LuEyeOff aria-hidden="true" />
              ) : (
                <LuEye aria-hidden="true" />
              )}
            </button>
          }
        />
        <AuthField
          id="register-confirm"
          label={t("auth.fields.confirmPassword", "Confirm password")}
          type={passwordVisibility.confirmPassword ? "text" : "password"}
          name="confirmPassword"
          autoComplete="new-password"
          required
          maxLength={PASSWORD_MAX_LENGTH}
          value={form.confirmPassword}
          onChange={handleChange}
          endAdornment={
            <button
              type="button"
              className="auth-field__password-toggle"
              onClick={() => togglePasswordVisibility("confirmPassword")}
              aria-label={buildPasswordToggleLabel(passwordVisibility.confirmPassword)}
              aria-pressed={passwordVisibility.confirmPassword}
              title={buildPasswordToggleLabel(passwordVisibility.confirmPassword)}
            >
              {passwordVisibility.confirmPassword ? (
                <LuEyeOff aria-hidden="true" />
              ) : (
                <LuEye aria-hidden="true" />
              )}
            </button>
          }
        />

        <label className="auth-checkbox auth-checkbox--stacked">
          <input
            type="checkbox"
            name="terms"
            required
            checked={form.terms}
            onChange={handleChange}
          />
          <span>
            {t("auth.register.termsPrefix", "I agree to the")}{" "}
            <Link to="/policies">{t("auth.register.termsLink", "Legal policies")}</Link>
            {t("auth.register.termsSuffix", ".")}
          </span>
        </label>

        {(error || success) && (
          <div className={`auth-message${error ? " auth-message--error" : " auth-message--success"}`}>
            {error || success}
          </div>
        )}

        <button type="submit" className="auth-button auth-button--primary" disabled={isSubmitting}>
          {isSubmitting
            ? t("auth.register.primaryCtaLoading", "Signing up...")
            : t("auth.register.primaryCta", "Sign up")}
        </button>
        <button
          type="button"
          className="auth-button auth-button--secondary"
          onClick={handleGoogleSignup}
          disabled={isSubmitting || isGoogleLoading}
        >
          <FcGoogle className="auth-button__icon" />
          {t("auth.register.googleCta", "Sign up with Google")}
        </button>
      </form>
    </AuthLayout>
  );
};

export default RegisterPage;
